// Plan-proposal runs for one opportunity (ENG-1828): create enqueues the
// drafting agent, show/index feed the planning modal's polling (ENG-1822).
// The proposal payload mirrors the submit_plan tool schema in
// GrantSuite::DraftPlanProposal; that tool definition is the contract.

export type PlanProposalStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed"
  | "retrying"
  | "needs_input";

// One plan-mode question from a needs_input run (ENG-1843): the agent
// parked because the answer would materially change the plan.
export type PlanProposalQuestion = {
  question: string;
  reason?: string | null;
};

export type PlanAnswer = {
  question: string;
  answer: string;
};

export type PlanProposalTask = {
  title: string;
  description?: string | null;
  source_passage?: string | null;
};

export type PlanProposalMilestone = {
  title: string;
  description?: string | null;
  target_timeframe?: string | null;
  source_passage?: string | null;
  tasks?: PlanProposalTask[];
};

export type PlanProposalRequiredDocument = {
  name: string;
  description?: string | null;
  source_passage?: string | null;
};

export type PlanProposalRequirement = {
  description: string;
  category?: string | null;
  source_passage?: string | null;
};

export type PlanProposalScoringCriterion = {
  name: string;
  weight?: string | null;
  description?: string | null;
  source_passage?: string | null;
};

export type PlanProposalPostAwardItem = {
  title: string;
  description?: string | null;
  source_passage?: string | null;
};

export type PlanProposal = {
  summary?: string | null;
  // Not yet emitted by the drafting agent; typed ahead so the review step's
  // proposed-title field lights up the moment the backend supplies one.
  proposed_title?: string | null;
  milestones?: PlanProposalMilestone[];
  required_documents?: PlanProposalRequiredDocument[];
  requirements?: PlanProposalRequirement[];
  scoring?: { criteria?: PlanProposalScoringCriterion[] } | null;
  post_award?: { items?: PlanProposalPostAwardItem[] } | null;
};

export type PlanProposalRun = {
  id: number;
  status: PlanProposalStatus;
  proposal: PlanProposal | null;
  questions: PlanProposalQuestion[] | null;
  error_message: string | null;
  model: string | null;
  duration_ms: number | null;
  created_at: string;
  // Set when this run's reviewed plan was confirmed into real records: the
  // server-side double-confirm guard, and the modal's signal not to reseed
  // the review editor from this run.
  confirmed_at?: string | null;
};

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

function planProposalsPath(municipalityId: number, opportunityId: number): string {
  return `/api/v1/municipalities/${municipalityId}/grant_opportunities/${opportunityId}/plan_proposals`;
}

export async function listPlanProposals(
  municipalityId: number,
  opportunityId: number,
): Promise<PlanProposalRun[]> {
  const response = await fetch(planProposalsPath(municipalityId, opportunityId), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error("Failed to load plan proposals");
  }
  return response.json();
}

// 202 with the in-flight run: either the one this call enqueued or one that
// was already running (the backend never runs two drafts concurrently).
export async function createPlanProposal(
  municipalityId: number,
  opportunityId: number,
): Promise<PlanProposalRun> {
  const response = await fetch(planProposalsPath(municipalityId, opportunityId), {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? "Failed to start plan draft");
  }
  return response.json();
}

// 202 with the continuation run: a fresh run carrying the parked run's Q&A
// as context, or an already in-flight run (the backend dedupes under a row
// lock — but the dedupe can return an unrelated in-flight run and drop the
// answers, ENG-1934; the modal prechecks the head run before posting to
// narrow that window). Blank answers are dropped server-side; each answer
// is truncated at 2,000 characters.
export async function submitPlanAnswers(
  municipalityId: number,
  opportunityId: number,
  planProposalId: number,
  answers: PlanAnswer[],
): Promise<PlanProposalRun> {
  const response = await fetch(
    `${planProposalsPath(municipalityId, opportunityId)}/${planProposalId}/answers`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ plan_proposal: { answers } }),
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.errors?.[0] ?? body.error ?? "Failed to send answers");
  }
  return response.json();
}

// Files-only continuation of a parked run: the user added package files
// instead of typing answers, so the batch is intentionally empty and the
// explicit flag keeps the server from 422ing it. Same endpoint and dedupe
// semantics as submitPlanAnswers.
export async function continuePlanWithFiles(
  municipalityId: number,
  opportunityId: number,
  planProposalId: number,
): Promise<PlanProposalRun> {
  const response = await fetch(
    `${planProposalsPath(municipalityId, opportunityId)}/${planProposalId}/answers`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ plan_proposal: { files_only: true } }),
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.errors?.[0] ?? body.error ?? "Failed to continue the draft");
  }
  return response.json();
}

export async function getPlanProposal(
  municipalityId: number,
  opportunityId: number,
  planProposalId: number,
): Promise<PlanProposalRun> {
  const response = await fetch(
    `${planProposalsPath(municipalityId, opportunityId)}/${planProposalId}`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok) {
    throw new Error("Failed to load plan proposal");
  }
  return response.json();
}
